/**
 * M0 — skeleton and deploy.
 *
 * This is a placeholder page, not the game. Its only job is to prove the whole
 * pipeline works end to end: TypeScript compiles, Vite bundles, three.js loads,
 * WebGL renders, and Cloudflare Pages serves it.
 *
 * If this page renders a spinning orange wireframe on the live URL, then every
 * technical assumption in docs/05-tech-stack.md is confirmed working on real
 * hardware, and M1 (movement) can start on solid ground.
 */

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Build info — proves which deploy you are actually looking at
// ---------------------------------------------------------------------------

const buildTimeEl = document.getElementById('build-time');
if (buildTimeEl) {
  const built = new Date(__BUILD_TIME__);
  buildTimeEl.textContent = built.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const rendererStatusEl = document.getElementById('renderer-status');

function setRendererStatus(text: string, ok: boolean): void {
  if (!rendererStatusEl) return;
  rendererStatusEl.textContent = text;
  rendererStatusEl.className = ok ? 'ok' : '';
}

// ---------------------------------------------------------------------------
// Minimal three.js scene
// ---------------------------------------------------------------------------

const canvas = document.getElementById('canvas');

if (!(canvas instanceof HTMLCanvasElement)) {
  setRendererStatus('canvas missing', false);
  throw new Error('Canvas element #canvas not found');
}

let renderer: THREE.WebGLRenderer;

try {
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
  });
} catch (err) {
  // WebGL can be unavailable: disabled in settings, blocked by a driver, or
  // running in a browser without hardware acceleration. Fail visibly rather
  // than silently showing a blank page.
  setRendererStatus('WebGL unavailable', false);
  throw err;
}

renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);

const scene = new THREE.Scene();

const camera = new THREE.PerspectiveCamera(
  50,
  window.innerWidth / window.innerHeight,
  0.1,
  100,
);
camera.position.z = 5.2;

// Wireframe icosahedron, in the same orange used for the host player.
// See docs/04-map-design.md — host is orange, guest is cyan.
const shape = new THREE.Mesh(
  new THREE.IcosahedronGeometry(1.9, 1),
  new THREE.MeshBasicMaterial({
    color: 0xff7a1a,
    wireframe: true,
    transparent: true,
    opacity: 0.22,
  }),
);
scene.add(shape);

// A second, slightly larger cyan shell rotating the other way.
const shell = new THREE.Mesh(
  new THREE.IcosahedronGeometry(2.6, 0),
  new THREE.MeshBasicMaterial({
    color: 0x22d3ee,
    wireframe: true,
    transparent: true,
    opacity: 0.09,
  }),
);
scene.add(shell);

// ---------------------------------------------------------------------------
// Resize handling
// ---------------------------------------------------------------------------

function onResize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;

  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
}

window.addEventListener('resize', onResize);

// ---------------------------------------------------------------------------
// Render loop
//
// Note: this uses a simple time-based rotation. The real game will NOT drive
// simulation from the render loop — it runs a fixed 64Hz timestep independent
// of framerate, because netcode requires deterministic physics.
// See docs/02-netcode.md ("The tick loop").
// ---------------------------------------------------------------------------

const clock = new THREE.Clock();
let framesRendered = 0;

function animate(): void {
  const t = clock.getElapsedTime();

  shape.rotation.x = t * 0.13;
  shape.rotation.y = t * 0.21;

  shell.rotation.x = t * -0.08;
  shell.rotation.z = t * 0.05;

  renderer.render(scene, camera);

  // Report success once we have actually drawn a few frames, rather than
  // just on construction — this confirms the GPU is really doing the work.
  if (framesRendered === 30) {
    setRendererStatus('WebGL OK', true);
  }
  framesRendered++;
}

renderer.setAnimationLoop(animate);

// eslint-disable-next-line no-console
console.log(
  `%c1v1 FPS %cM0 — skeleton\nbuilt ${__BUILD_TIME__}\nthree.js r${THREE.REVISION}`,
  'color:#ff7a1a;font-weight:bold',
  'color:#6b7280',
);
